"use client";

import React from "react";
import { motion } from "framer-motion";
import { Background } from "@/components/home/DarkstarBackground";
import { Scroll, Shield, Scale, Clock, RefreshCw, Mail, Lock, Feather, ShieldCheck, Globe } from "lucide-react";
import Link from "next/link";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.3,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.6, -0.05, 0.01, 0.99],
    },
  },
};

export default function TermsOfService() {
  return (
    <>
      <Background />
      <div className="relative min-h-screen py-24 px-4 sm:px-6 lg:px-8">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="max-w-4xl mx-auto"
        >
          <motion.div variants={itemVariants} className="text-center mb-12">
            <h1 className="text-xl tracking-tight text-primary mb-4">Terms of Service</h1>
            <p className="text-lg text-primary/70">
              Last updated: {new Date().toLocaleDateString()}
            </p>
          </motion.div>

          <motion.div variants={itemVariants} className="space-y-8 text-primary/80">
            {/* 1. Service Description */}
            <section className="bg-white/10 backdrop-blur-sm rounded-sm p-8 border border-white/20">
              <div className="flex items-center gap-3 mb-4">
                <Scroll className="h-6 w-6 text-primary" />
                <h2 className="text-md text-primary">Service Description</h2>
              </div>
              <p>
                Arcana Vibes (referred to as "we," "us," or "our") provides an AI-powered tarot reading platform designed to offer various tarot card reading types and personalized spiritual insights. Our platform includes both free trial and subscription-based features.
              </p>
              <p className="mt-4">
                Users begin with a free trial period that provides full access to all features. After the trial period, continued access requires an active subscription. Our subscription plans provide unlimited access to all reading types and features.
              </p>
            </section>

            {/* 2. User Responsibilities */}
            <section className="bg-white/10 backdrop-blur-sm rounded-sm p-8 border border-white/20">
              <div className="flex items-center gap-3 mb-4">
                <Shield className="h-6 w-6 text-primary" />
                <h2 className="text-md text-primary">User Responsibilities</h2>
              </div>
              <p>
                <strong>Eligibility:</strong> By using our services, you represent that you are at least the age of majority in your jurisdiction and that you have the right, authority, and capacity to enter into these Terms of Service. You agree not to use the service for any purpose prohibited by law or these Terms.
              </p>
              <p className="mt-4">
                <strong>Account Information:</strong> You agree to provide accurate and complete information during registration and to keep your account information current. You are responsible for maintaining the confidentiality of your login credentials and for all activities that occur under your account.
              </p>
              <p className="mt-4">
                <strong>Prohibited Uses:</strong> You agree not to use the Service in a manner that violates any laws, infringes any third party’s rights, or exploits the Service for unauthorized or commercial purposes. You must not impersonate others, transmit unsolicited promotions, or use the service to distribute illegal or harmful content. We reserve the right to suspend or terminate your account for violations of this section.
              </p>
            </section>

            {/* 3. Intellectual Property Rights */}
            <section className="bg-white/10 backdrop-blur-sm rounded-sm p-8 border border-white/20">
              <div className="flex items-center gap-3 mb-4">
                <Feather className="h-6 w-6 text-primary" />
                <h2 className="text-md text-primary">Intellectual Property Rights</h2>
              </div>
              <p>
                All content, features, and functionality of the Service, including but not limited to text, graphics, logos, images, software, and the selection and arrangement thereof, are the exclusive property of Arcana Vibes or its licensors. You may not reproduce, distribute, modify, or create derivative works of any material without our prior written permission.
              </p>
            </section>

            {/* 4. Token Usage & Billing */}
            <section className="bg-white/10 backdrop-blur-sm rounded-sm p-8 border border-white/20">
              <div className="flex items-center gap-3 mb-4">
                <Scale className="h-6 w-6 text-primary" />
                <h2 className="text-md text-primary">Token Usage & Billing</h2>
              </div>
              <p>
                <strong>Subscriptions and Monthly Tokens:</strong> If you subscribe to a plan, you authorize us (through our payment processor, Stripe) to charge your payment method the recurring subscription fee at the start of each billing cycle. You will receive Monthly Tokens that expire at the end of that billing cycle if unused. Unused Monthly Tokens do not roll over.
              </p>
              <p className="mt-4">
                <strong>Token Packs:</strong> You may also purchase Token Packs separately. These tokens do not expire and remain in your account until used or until your account is terminated for cause.
              </p>
              <p className="mt-4">
                <strong>Stripe Compliance:</strong> All payments are processed via Stripe. By making a purchase, you agree to Stripe’s terms, policies, and applicable laws. We may share transaction information with Stripe as needed to process payments, prevent fraud, or ensure regulatory compliance. If you violate Stripe’s terms or policies, we may suspend or terminate your access to the Service.
              </p>
              <p className="mt-4">
                <strong>Refunds and Price Changes:</strong> All fees and charges are non-refundable unless required by law. We do not issue refunds or credits for partially used subscription periods or unused tokens. We reserve the right to change our subscription or token pricing and billing policies at any time upon notice to you.
              </p>
              <p className="mt-4">
                <strong>Cancellations:</strong> You may cancel your subscription at any time, effective at the end of the current billing cycle. After cancellation, you retain access to your subscription features and Monthly Tokens until that billing cycle ends.
              </p>
            </section>

            {/* 5. Account Security and Responsibility */}
            <section className="bg-white/10 backdrop-blur-sm rounded-sm p-8 border border-white/20">
              <div className="flex items-center gap-3 mb-4">
                <Lock className="h-6 w-6 text-primary" />
                <h2 className="text-md text-primary">Account Security and Responsibility</h2>
              </div>
              <p>
                You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. Notify us immediately of any unauthorized use of your account or other security breaches. We are not liable for any loss or damage arising from your failure to safeguard your account information.
              </p>
            </section>

            {/* 6. Data and Privacy */}
            <section className="bg-white/10 backdrop-blur-sm rounded-sm p-8 border border-white/20">
              <div className="flex items-center gap-3 mb-4">
                <Shield className="h-6 w-6 text-primary" />
                <h2 className="text-md text-primary">Data and Privacy</h2>
              </div>
              <p>
                We are committed to protecting your privacy. We collect and use your information as described in our Privacy Policy, including reading history, token usage, and account details. We take reasonable measures to secure your data, but cannot guarantee absolute security. For more details, please review our{" "}
                <Link href="/privacy" className="text-primary underline hover:text-primary/90 transition-colors">
                  Privacy Policy
                </Link>
                .
              </p>
            </section>

            {/* 7. Disclaimer of Warranties & Limitations of Liability */}
            <section className="bg-white/10 backdrop-blur-sm rounded-sm p-8 border border-white/20">
              <div className="flex items-center gap-3 mb-4">
                <Clock className="h-6 w-6 text-primary" />
                <h2 className="text-md text-primary">Disclaimer of Warranties & Limitations of Liability</h2>
              </div>
              <p>
                <strong>Disclaimer of Warranties:</strong> The Service is provided on an "AS IS" and "AS AVAILABLE" basis, without warranties of any kind, express or implied. We do not guarantee that the Service will be uninterrupted, error-free, or secure. The tarot readings and interpretations are for entertainment and self-reflection purposes only and do not constitute professional advice.
              </p>
              <p className="mt-4">
                <strong>Limitations of Liability:</strong> To the fullest extent permitted by law, Arcana Vibes, its employees, affiliates, and partners are not liable for any indirect, incidental, special, consequential, or punitive damages, including loss of profits, data, or goodwill, arising from or related to your use of the Service.
              </p>
            </section>

            {/* 8. Indemnification */}
            <section className="bg-white/10 backdrop-blur-sm rounded-sm p-8 border border-white/20">
              <div className="flex items-center gap-3 mb-4">
                <ShieldCheck className="h-6 w-6 text-primary" />
                <h2 className="text-md text-primary">Indemnification</h2>
              </div>
              <p>
                You agree to defend, indemnify, and hold harmless Arcana Vibes and its officers, directors, employees, and agents from and against any claims, liabilities, damages, judgments, awards, losses, costs, expenses, or fees arising out of or relating to your violation of these Terms or your use of the Service.
              </p>
            </section>

            {/* 9. Governing Law & Jurisdiction */}
            <section className="bg-white/10 backdrop-blur-sm rounded-sm p-8 border border-white/20">
              <div className="flex items-center gap-3 mb-4">
                <Globe className="h-6 w-6 text-primary" />
                <h2 className="text-md text-primary">Governing Law & Jurisdiction</h2>
              </div>
              <p>
                These Terms and any dispute arising out of them shall be governed by and construed in accordance with the laws of [Your Governing Jurisdiction], without regard to conflict of laws principles. Any legal action or proceeding shall be brought exclusively in the federal or state courts located in [Your Governing Jurisdiction], and you consent to personal jurisdiction and venue therein.
              </p>
            </section>

            {/* 10. Changes to the Terms */}
            <section className="bg-white/10 backdrop-blur-sm rounded-sm p-8 border border-white/20">
              <div className="flex items-center gap-3 mb-4">
                <RefreshCw className="h-6 w-6 text-primary" />
                <h2 className="text-md text-primary">Changes to the Terms</h2>
              </div>
              <p>
                We reserve the right to modify or replace these Terms of Service at any time. Any changes will be effective immediately upon posting. It is your responsibility to review the Terms periodically. Your continued use of the Service after changes are posted constitutes acceptance of the updated Terms. If you do not agree, you must discontinue use of the Service.
              </p>
            </section>

            {/* 11. Contact Information */}
            <section className="bg-white/10 backdrop-blur-sm rounded-sm p-8 border border-white/20">
              <div className="flex items-center gap-3 mb-4">
                <Mail className="h-6 w-6 text-primary" />
                <h2 className="text-md text-primary">Contact Information</h2>
              </div>
              <p>
                If you have any questions or concerns about these Terms of Service, please contact us at{" "}
                <a href="mailto:support@arcanavibes.com" className="text-primary underline">
                  support@arcanavibes.com
                </a>
                .
              </p>
            </section>
          </motion.div>
        </motion.div>
      </div>
    </>
  );
}